import { useEffect, useState } from "react"

import { Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import Button from "../../../../../Components/Button"
import Modal from "../../../../../Components/Modal"
import Icon from "../../../../../Icon"
import { conditionsConflict, crossRuleConflict, ruleConditionsSummary } from "../utils/ruleSummary"
import ConditionsBuilder from "./ConditionsBuilder/ConditionsBuilder"

import styles from "./ruleModal.module.less"

const { Text } = Typography

// Add / edit an auto-submission exception: one or more conditions (category / amount)
// that carve matching disputes OUT of auto-submission and send them to the team for
// manual review instead. Holding a dispute back is the only thing an exception can do
// (there is no auto-submit action to choose, since that is already the default for
// everything). Local form state only; commits via onSave (the parent handles the
// confirm + notification).
const CategoryExceptionModal = ({
  visible,
  onClose,
  onSave,
  initial,
  categoryOptions,
  otherRules = []
}) => {
  const { t } = useTranslation()
  // The shared Modal switches to its bottom-sheet layout via this PROP (not redux):
  // without it, centered rendering collides with the mobile sheet CSS and the whole
  // popup lands off-screen at phone widths.
  const isMobile = useSelector((state) => state.nav.isMobile)
  const [conditions, setConditions] = useState([])

  // Reset the form each time the modal opens (fresh add, or prefilled edit).
  useEffect(() => {
    if (!visible) return
    setConditions(
      Array.isArray(initial?.conditions) && initial.conditions.length
        ? initial.conditions.map((c) => ({ ...c }))
        : [{ field: "category", op: "is", value: "" }]
    )
  }, [visible, initial])

  // Every condition must have a value before the rule can be saved, the set must be
  // satisfiable on its own (a rule ANDs its conditions, so contradictions can never
  // match), AND it must not duplicate or be swallowed by an exception that already
  // exists, which would leave a rule in the list that can never change an outcome.
  const filledConditions = conditions.filter((c) => c.value !== "" && c.value != null)
  const conflict = conditionsConflict(conditions, t) || crossRuleConflict(conditions, otherRules, t)
  const canSave = filledConditions.length > 0 && !conflict
  const handleSave = () => {
    if (!canSave) return
    // Every rule is a never-auto-submit exception (manual review).
    onSave({ id: initial?.id, action: "manual", conditions: filledConditions })
  }

  // Same modal shell as the Alerts Auto-refund setup (AutoRefundPage.js): clear-bg
  // content, description under the title, Cancel/Save inside the body.
  return (
    <Modal
      visible={visible}
      isMobile={isMobile}
      handleClose={onClose}
      title={initial ? t("Edit exception") : t("Add exception")}
      footer={null}
      destroyOnClose
      width={680}
      closeIcon={<Icon type="cross_small" />}
      classNames={{ content: "clear-bg" }}
      className="change-refund-form-modal"
    >
      <Text type="secondary">
        {t(
          "Disputes that match every condition below are held back from auto-submission and sent to your team for manual review instead. Everything else keeps being submitted automatically."
        )}
      </Text>
      <div className={styles.root} style={{ marginTop: 16 }}>
        <div className={styles.field}>
          <Text strong>{t("Conditions")}</Text>
          <Text type="secondary" className={styles.hint}>
            {t(
              "Match on category or amount. A dispute must match every condition for the exception to apply."
            )}
          </Text>
          <ConditionsBuilder
            conditions={conditions}
            onChange={setConditions}
            categoryOptions={categoryOptions}
            includeNetworkIssuer={false}
          />
        </div>

        <div className={styles.summary}>
          <Text strong className={styles.summaryTitle}>
            {t("Summary")}
          </Text>
          <Text>
            <Text strong>{t("Action")}:</Text> {t("Hold back for manual review")}
          </Text>
          <Text>
            <Text strong>{t("When")}:</Text> {ruleConditionsSummary(conditions, t)}
          </Text>
        </div>
      </div>

      {conflict ? (
        <Flex align="center" gap={8} style={{ marginTop: 16 }}>
          <Icon type="alert_triangle_error" style={{ fontSize: 20 }} />
          <Text type="danger">{conflict}</Text>
        </Flex>
      ) : null}

      <Flex gap={16} justify="end" style={{ padding: "0 3px 3px 0", marginTop: 24 }}>
        <Button size="large" htmlType="button" onClick={onClose} text={t("Cancel")} />
        <Button
          size="large"
          type="primary"
          disabled={!canSave}
          onClick={handleSave}
          text={t("Save exception")}
        />
      </Flex>
    </Modal>
  )
}

export default CategoryExceptionModal
